import {IOverlayParameter, IPointGeometry} from "@/types/map";
import {loadModules} from "esri-loader";

export default class BubbleLayer {
  private static intances: Map<string, BubbleLayer>;

  public static getInstance(view: __esri.SceneView | __esri.MapView) {
    const id = view.container.id;
    if (!BubbleLayer.intances) {
      BubbleLayer.intances = new Map();
    }
    let instance = BubbleLayer.intances.get(id);
    if (!instance) {
      instance = new BubbleLayer(view);
      BubbleLayer.intances.set(id, instance);
    }
    return instance;
  }

  private view!: __esri.SceneView | __esri.MapView;
  private animateFeatureLayre!: __esri.FeatureLayer;
  private Color!: __esri.ColorConstructor;
  private Graphic!: __esri.GraphicConstructor;

  private animateSpeed = 0.8;
  private symbolMaxSize = 30;
  private symbolMinSize = 1;
  private symbolSize = 1;
  private symbolStartColor!: __esri.Color;
  private symbolEndColor!: __esri.Color;
  private animating = false;

  private constructor(view: __esri.SceneView | __esri.MapView) {
    this.view = view;
  }

  public async initialize(param?: {
    minSize?: number;
    maxSize?: number;
    speed?: number;
  }) {
    this.symbolMinSize = param?.minSize ?? 1;
    this.symbolMaxSize = param?.maxSize ?? 30;
    this.animateSpeed = param?.speed ?? 0.8;

    if (!this.animateFeatureLayre) {
      type MapModules = [
        typeof import("esri/layers/FeatureLayer"),
        typeof import("esri/Graphic"),
        typeof import("esri/Color")
      ];
      const [FeatureLayer, Graphic, Color] = await (loadModules([
        "esri/layers/FeatureLayer",
        "esri/Graphic",
        "esri/Color"
      ]) as Promise<MapModules>);

      this.Graphic = Graphic;
      this.Color = Color;
      this.symbolStartColor = new this.Color([255, 0, 0]);
      this.symbolEndColor = new this.Color([255, 0, 0, 0.4]);
      let symbol: any;
      if (this.view.type == "2d") {
        symbol = {
          type: "simple-marker", // autocasts as new SimpleMarkerSymbol()
          style: "circle",
          color: [255, 0, 0, 0.8],
          size: this.symbolMinSize,
          outline: {
            // autocasts as new SimpleLineSymbol()
            color: [255, 0, 0],
            width: 0 // points
          }
        };
      } else {
        symbol = {
          type: "point-3d",
          symbolLayers: [
            {
              // type: "object",
              // width: this.symbolMinSize,
              // height: this.symbolMinSize,
              // depth: this.symbolMinSize,
              // resource: {primitive: "sphere"}
              type: "icon",
              size: this.symbolMinSize,
              resource: {primitive: "circle"}
            }
          ]
        };
      }
      this.animateFeatureLayre = new FeatureLayer({
        source: [],
        objectIdField: "OBJECTID",
        geometryType: "point",
        outFields: ["*"],
        spatialReference: {wkid: 4326},
        fields: [
          {name: "OBJECTID", type: "oid"},
          {name: "name", type: "string"},
          {name: "type", type: "string"},
          {name: "id", type: "string"}
        ],
        //使其贴在地面上
        elevationInfo: {mode: "on-the-ground"},
        renderer: {
          type: "simple",
          symbol: symbol
        } as any
      });
      this.view.map.add(this.animateFeatureLayre);
    }
    this.animating = false;
    this.animateSymbol();
  }

  public async addPoints(overlayParam: IOverlayParameter) {
    this.animating = false;
    this.symbolSize = this.symbolMinSize;

    const graphics = overlayParam.overlays.map((overlay) => {
      const overlayGeometry = overlay.geometry as IPointGeometry;
      const graphic = new this.Graphic({
        geometry: {
          type: "point",
          x: overlayGeometry.x,
          y: overlayGeometry.y
        } as any,
        attributes: {...overlay.fields}
      });
      return graphic;
    });
    const edits = {addFeatures: graphics};
    await this.animateFeatureLayre.applyEdits(edits);

    this.animating = true;
  }

  public async removeAll() {
    this.animating = false;

    const allFeatures = await this.animateFeatureLayre.queryFeatures();
    const edits = {deleteFeatures: allFeatures.features};
    await this.animateFeatureLayre.applyEdits(edits);

    this.symbolSize = this.symbolMinSize;
  }

  public async remove(type: string) {
    let query = this.animateFeatureLayre.createQuery();
    query.where = "type ='" + type + "'";
    query.outFields = ["*"];
    const selectFeatures = await this.animateFeatureLayre.queryFeatures(query);
    const edits = {deleteFeatures: selectFeatures.features};
    await this.animateFeatureLayre.applyEdits(edits);
  }

  private animateSymbol() {
    if (this.animating) {
      this.symbolSize +=
        this.symbolSize > this.symbolMaxSize * 0.2 &&
        this.symbolSize < this.symbolMaxSize * 0.8
          ? this.animateSpeed
          : this.animateSpeed * 2;
      // this.bubbleSize += this.bubbleSpeed;
      if (this.symbolSize > this.symbolMaxSize) {
        this.symbolSize = this.symbolMinSize;
      }
      const blendColor = this.Color.blendColors(
        this.symbolStartColor,
        this.symbolEndColor,
        this.symbolSize / this.symbolMaxSize
      );
      let symbol: any;
      if (this.view.type == "2d") {
        symbol = {
          type: "simple-marker", // autocasts as new SimpleMarkerSymbol()
          style: "circle",
          color: blendColor,
          size: this.symbolSize,
          outline: {
            // autocasts as new SimpleLineSymbol()
            color: [255, 0, 0],
            width: 0 // points
          }
        };
      } else {
        symbol = {
          type: "point-3d",
          symbolLayers: [
            {
              // type: "object",
              // width: this.symbolMinSize,
              // height: this.symbolMinSize,
              // depth: this.symbolMinSize,
              // resource: {primitive: "sphere"}
              type: "icon",
              size: this.symbolSize,
              resource: {primitive: "circle"},
              material: {color: blendColor}
            }
          ]
        };
      }
      this.animateFeatureLayre.renderer = {
        type: "simple",
        symbol
      } as any;
    }

    setTimeout(() => {
      requestAnimationFrame(() => {
        this.animateSymbol();
      });
    }, 1000 / 30);
  }
}
